import {
    useEffect,
    useMemo,
    useState,
    type HTMLAttributes,
    type Key,
    type ReactNode
} from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useStableQuery } from '../../db/useStableQuery';
import {
    useForm,
    useWatch,
    Controller,
    type Control,
    type Resolver
} from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import Box from '@mui/material/Box';
import TextField from '@mui/material/TextField';
import Autocomplete, {
    type AutocompleteRenderInputParams,
    type AutocompleteRenderOptionState
} from '@mui/material/Autocomplete';
import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';
import Chip from '@mui/material/Chip';
import Alert from '@mui/material/Alert';
import AlertTitle from '@mui/material/AlertTitle';
import dayjs from 'dayjs';
import { useRxCollection, useLiveRxQuery } from 'rxdb/plugins/react';
import {
    timeEntryInputSchema,
    type TimeEntryDocType,
    type TimeEntryType,
    type ProjectDocType
} from '@ast-data/shared';
import { useAbility, scopedSubject } from '../auth/abilityContext';
import { projectMeasureLabel } from '../projects/projectTitle';
import { useValidationMessage } from '../../i18n/validationMessages';
import { useSavedToast } from '../../components/toastContext';
import DateStringField from '../../components/DateStringField';
import TimeStringField from '../../components/TimeStringField';
import FormDialog from '../../components/FormDialog';
import FormDialogAppBar from '../../components/FormDialogAppBar';
import FormDialogFooter from '../../components/FormDialogFooter';
import FormSection, {
    FormSectionsBody,
    FormSectionNotice
} from '../../components/FormSection';
import FormRow from '../../components/FormRow';
import { useUnsavedCloseGuard } from '../../components/useUnsavedCloseGuard';
import UnsavedChangesDialog from '../../components/UnsavedChangesDialog';
import { saveTimeEntry } from './timeEntryMutations';
import TimeEntryOverlapNotice from './TimeEntryOverlapNotice';
import {
    useTimeEntryFormNameOptions,
    useTimeEntryDayBookings,
    type TimeEntryDayBooking
} from './useTimeEntryNameOptions';
import { useTimeEntryOverlapsWithEach } from './useTimeEntryOverlaps';
import {
    useTimeEntryDraftWarnings,
    timeEntryWarningFields
} from './timeEntryFlags';
import { type TimeEntryVariant } from './timeEntryVariant';
import {
    ALL_DAY_END,
    ALL_DAY_START,
    isAllDaySpan,
    latestBookableTime
} from './timeMath';
import { useActiveUsers } from '../users/useActiveUsers';
import { employmentWindow } from '../users/employment';
import { useUserLabel } from '../users/useUserLabel';
import { useAccountResolver } from '../users/personUserId';

import { compareNatural } from '../../utils/compare';
import { chipClipboardProps } from '../../components/chipClipboard';
import TabSuggestAutocomplete from '../../components/TabSuggestAutocomplete';

type FormValues = {
    /**
     * Not an input — the view fixes it. It rides in the form's values because
     * the shared schema BRANCHES on it (a Gerät may be booked without times, a
     * Mitarbeiter may not) and the resolver only sees what the form holds.
     */
    entryType: TimeEntryType;
    date: string;
    /**
     * A LIST: a new Mitarbeiter entry may be booked for a whole crew at once,
     * each name becoming an entry of its own. Every other shape (editing one
     * entry, naming a Gerät) is the same list with at most one name in it.
     */
    workerName: string[];
    quantity: number;
    userId: string;
    startTime: string;
    endTime: string;
    pauseMinutes: number;
    note: string;
    phaseNumber: string;
};

function todayIso(): string {
    return new Date().toISOString().slice(0, 10);
}

function emptyValues(
    entryType: TimeEntryType,
    defaultDate?: string
): FormValues {
    return {
        entryType,
        date: defaultDate || todayIso(),
        workerName: [],
        quantity: 1,
        userId: '',
        startTime: '',
        endTime: '',
        pauseMinutes: 0,
        note: '',
        phaseNumber: ''
    };
}

function toValues(
    entry: TimeEntryDocType,
    entryType: TimeEntryType
): FormValues {
    return {
        entryType,
        date: entry.date,
        workerName: [entry.workerName],
        // Rows written before the Anzahl existed carry none.
        quantity: entry.quantity || 1,
        userId: entry.userId,
        startTime: entry.startTime,
        endTime: entry.endTime,
        pauseMinutes: entry.pauseMinutes,
        note: entry.note,
        phaseNumber: entry.phaseNumber
    };
}

/**
 * The values a refused save was about. The verdict comes from the server-side
 * rules and cannot be recomputed while typing, so it is remembered together with
 * the values it was reached for and shown only while those are still on screen —
 * which is what makes a dismiss button unnecessary.
 */
function saveErrorKey(values: {
    date: string;
    workerName: readonly string[];
    startTime: string;
    endTime: string;
}): string {
    return [
        values.date,
        values.workerName.map((name) => name.trim()).join(','),
        values.startTime,
        values.endTime
    ].join('|');
}

/** A stable empty list, so a variant without drafts doesn't re-ask every render. */
const NO_DRAFTS: never[] = [];

/**
 * The form holds a LIST of names; the shared schema knows ONE entry. So the
 * shape is validated with the first picked name standing in for the field — the
 * others differ in nothing the schema looks at, and an empty selection still
 * trips its `required` rule. Each name is checked again by the save path.
 */
const entryResolver = zodResolver(
    timeEntryInputSchema
) as unknown as Resolver<FormValues>;

const resolver: Resolver<FormValues> = async (values, context, options) => {
    const result = await entryResolver(
        {
            ...values,
            workerName: values.workerName[0] ?? ''
        } as unknown as FormValues,
        context,
        options
    );
    if (Object.keys(result.errors).length > 0) {
        return { values: {}, errors: result.errors };
    }
    // Hand back the picked names, not the single stand-in the schema saw.
    return {
        values: {
            ...(result.values as FormValues),
            workerName: values.workerName
        },
        errors: {}
    };
};

function projectLabel(project: ProjectDocType): string {
    return projectMeasureLabel(project) || project.shortTitle || project.id;
}

interface TimeEntryNameFieldProps {
    control: Control<FormValues>;
    variant: TimeEntryVariant;
    options: string[];
    multiple: boolean;
    /** What a name's day already holds, and whether it collides — see below. */
    bookedTimes: (name: string) => TimeEntryDayBooking;
}

/**
 * The Mitarbeiter / Gerät field. One form value (always a list of names), three
 * shapes:
 *
 * - Mitarbeiter, new: several accounts at once — a crew works the same span.
 * - Mitarbeiter, edit: ONE account. An entry belongs to one person.
 * - Gerät: free text, because a Gerät has no account to be picked from.
 *
 * Free text is gone from the Mitarbeiter field on purpose: every spelling of a
 * name was a person of its own to the totals and to the Crewmeister-Abgleich.
 */
function TimeEntryNameField({
    control,
    variant,
    options,
    multiple,
    bookedTimes
}: TimeEntryNameFieldProps) {
    const intl = useIntl();
    const validationMessage = useValidationMessage();
    // Each option carries what that name is already booked for THIS day, so a
    // double-booking is seen while picking rather than when Speichern refuses
    // it.
    const renderOption = (
        props: HTMLAttributes<HTMLLIElement> & { key: Key },
        option: string,
        state: AutocompleteRenderOptionState
    ) => {
        const { key, ...liProps } = props;
        const booking = bookedTimes(option);
        return (
            <Box component="li" key={key} {...liProps}>
                {/* The list stays open while a crew is picked, so it has to say
                    itself which names are already in — on a phone the chips sit
                    above the open list and the keyboard, out of sight. */}
                {multiple && (
                    <Checkbox
                        checked={state.selected}
                        size="small"
                        sx={{ mr: 1, p: 0.5 }}
                    />
                )}
                {option}
                {booking.spans.length > 0 && (
                    <Box
                        component="span"
                        sx={{
                            ml: 1,
                            /**
                             * The times are the whole reason the option is
                             * greyed out, so they say so themselves.
                             */
                            color: booking.conflicts
                                ? 'error.main'
                                : 'text.secondary'
                        }}
                    >
                        ({booking.spans.join(', ')})
                    </Box>
                )}
            </Box>
        );
    };

    return (
        <Controller
            name="workerName"
            control={control}
            render={({ field, fieldState }) => {
                const renderInput = (params: AutocompleteRenderInputParams) => (
                    <TextField
                        {...params}
                        label={intl.formatMessage({ id: variant.nameLabelId })}
                        error={!!fieldState.error}
                        helperText={
                            fieldState.error
                                ? validationMessage(fieldState.error.message)
                                : multiple
                                  ? intl.formatMessage({
                                        id: 'timeEntry.workerNames.help'
                                    })
                                  : undefined
                        }
                    />
                );
                if (multiple) {
                    return (
                        <Autocomplete
                            multiple
                            // Picking a crew is several picks in a row — closing
                            // the list after each one would reopen it each time.
                            disableCloseOnSelect
                            /**
                             * Fifty-odd accounts are typed at, not scrolled
                             * through: "V" highlights the first match, so Enter
                             * takes it. Selecting empties the input again (MUI
                             * does that in multiple mode), which makes the next
                             * name the next two keystrokes.
                             */
                            autoHighlight
                            options={options}
                            // Already booked across these times: the save path
                            // would refuse the entry anyway. autoHighlight skips
                            // disabled options, so Enter never lands on one.
                            getOptionDisabled={(option) =>
                                bookedTimes(option).conflicts
                            }
                            value={field.value}
                            onChange={(_event, value) => field.onChange(value)}
                            {...chipClipboardProps({
                                value: field.value,
                                getLabel: (name: string) => name,
                                options,
                                isOptionDisabled: (name) =>
                                    bookedTimes(name).conflicts,
                                onChange: field.onChange
                            })}
                            onBlur={field.onBlur}
                            renderInput={renderInput}
                            renderOption={renderOption}
                            /**
                             * A name picked BEFORE the times moved onto someone
                             * else's shift is still in the field, and its option
                             * is now greyed out where nobody looks. The chip is
                             * where one sees which name the conflict panel is
                             * about.
                             */
                            renderValue={(names, getItemProps) =>
                                names.map((name, index) => {
                                    const { key, ...chipProps } = getItemProps({
                                        index
                                    });
                                    return (
                                        <Chip
                                            key={key}
                                            size="small"
                                            label={name}
                                            color={
                                                bookedTimes(name).conflicts
                                                    ? 'error'
                                                    : undefined
                                            }
                                            {...chipProps}
                                        />
                                    );
                                })
                            }
                        />
                    );
                }
                if (variant.namesFromAccounts) {
                    return (
                        <Autocomplete
                            autoHighlight
                            options={options}
                            getOptionDisabled={(option) =>
                                bookedTimes(option).conflicts
                            }
                            value={field.value[0] ?? null}
                            onChange={(_event, value) =>
                                field.onChange(value ? [value] : [])
                            }
                            onBlur={field.onBlur}
                            renderInput={renderInput}
                            renderOption={renderOption}
                        />
                    );
                }
                return (
                    <TabSuggestAutocomplete
                        freeSolo
                        options={options}
                        onTake={(option) => field.onChange([option])}
                        // A Gerät cannot be on two sites at once either. Free
                        // text can still name one — the save path is what
                        // actually refuses; this only keeps the LIST honest.
                        getOptionDisabled={(option) =>
                            bookedTimes(option).conflicts
                        }
                        inputValue={field.value[0] ?? ''}
                        onInputChange={(_event, value) =>
                            field.onChange(value ? [value] : [])
                        }
                        onBlur={field.onBlur}
                        renderInput={renderInput}
                        renderOption={renderOption}
                    />
                );
            }}
        />
    );
}

interface TimeEntryFormDialogProps {
    /** Which Zeiterfassung the entry belongs to — labels, Pause, user link. */
    variant: TimeEntryVariant;
    open: boolean;
    onClose: () => void;
    /**
     * The fixed project a NEW entry belongs to (field tool). Omit in the office
     * overview to get a project picker instead. Ignored when editing.
     */
    projectId?: string;
    entry?: TimeEntryDocType | null;
    defaultDate?: string;
}

/**
 * The full Zeiterfassung editor — a fullscreen record dialog like every other
 * entity's. The grid's INLINE editing is the quick path for a correction; this
 * is the one that also picks a project.
 *
 * Everything sits in ONE section box, with no jump-to-section nav: with seven
 * fields a rail would be navigation to nowhere.
 */
export default function TimeEntryFormDialog({
    variant,
    open,
    onClose,
    projectId,
    entry,
    defaultDate
}: TimeEntryFormDialogProps) {
    const intl = useIntl();
    const collection = useRxCollection<TimeEntryDocType>('timeEntries');
    const validationMessage = useValidationMessage();
    const notifySaved = useSavedToast();
    const ability = useAbility();
    const isEdit = !!entry;

    const fixedProjectId = isEdit
        ? (entry?.projectId ?? '')
        : (projectId ?? '');
    const [selectedProjectId, setSelectedProjectId] = useState('');
    const [projectError, setProjectError] = useState(false);
    const activeProjectId = fixedProjectId || selectedProjectId;
    const showProjectSelect = !fixedProjectId;

    const projectsQuery = useStableQuery<ProjectDocType>({});
    const { results: projectsDocs } = useLiveRxQuery<ProjectDocType>({
        collection: 'projects',
        query: projectsQuery
    });
    const projects = useMemo(
        () => projectsDocs.map((doc) => doc.toMutableJSON() as ProjectDocType),
        [projectsDocs]
    );
    // Several at once only for a new entry: an existing one is one person's.
    const multipleNames = variant.namesFromAccounts && !isEdit;
    // The name -> account rule, shared with the save path and the Abgleich.
    const resolveAccount = useAccountResolver();

    const projectOptions = useMemo(
        () =>
            [...projects]
                .filter((project) =>
                    ability.can(
                        'create',
                        scopedSubject('TimeEntry', { projectId: project.id })
                    )
                )
                .sort((firstOption, secondOption) =>
                    compareNatural(
                        projectLabel(firstOption),
                        projectLabel(secondOption)
                    )
                ),
        [projects, ability]
    );
    const selectedProject =
        projectOptions.find((project) => project.id === selectedProjectId) ??
        null;

    const {
        register,
        handleSubmit,
        reset,
        control,
        setValue,
        formState: { errors, isSubmitting, isDirty }
    } = useForm<FormValues>({
        resolver,
        defaultValues: emptyValues(variant.type)
    });

    // Why a save was refused, tagged with the values it was refused for — see
    // saveErrorKey.
    const [saveError, setSaveError] = useState<{
        key: string;
        /** A node, not a string: a refused double-booking carries a link to the
         *  entry that blocked it (TimeEntryOverlapNotice). */
        message: ReactNode;
    } | null>(null);

    useEffect(() => {
        if (open) {
            reset(
                entry
                    ? toValues(entry, variant.type)
                    : emptyValues(variant.type, defaultDate)
            );
            // These three belong WITH the reset above, and `reset` publishes to
            // react-hook-form's own subscribers, so the block cannot move into
            // the render pass.
            // eslint-disable-next-line react-hooks/set-state-in-effect
            setSaveError(null);
            setProjectError(false);
            setSelectedProjectId('');
        }
    }, [open, entry, defaultDate, variant.type, reset]);

    const watchedDate = useWatch({ control, name: 'date' });
    // Only who was employed on the work day (see users/employment).
    const users = useActiveUsers(employmentWindow(watchedDate ?? ''));
    const nameOf = useUserLabel();
    // What the form holds stays an option: the edited entry's own name, and a
    // crew picked before the date was changed (so it shows, and can be taken
    // out, rather than vanishing from the list).
    const pickedNames: unknown = useWatch({ control, name: 'workerName' });
    const nameOptions = useTimeEntryFormNameOptions(
        variant,
        users,
        nameOf,
        Array.isArray(pickedNames)
            ? (pickedNames as string[])
            : `${pickedNames ?? entry?.workerName ?? ''}`
    );
    // Capped at the bookable bound (now + buffer) when the workday is today — an
    // entry may already name the hour it will end.
    const bookableUntil = latestBookableTime();
    const maxTime =
        watchedDate === todayIso() && bookableUntil
            ? dayjs(`2000-01-01T${bookableUntil}`)
            : undefined;

    // Live ArbZG warnings, the same rules the grid flags a saved row with.
    // Advisory only: statutory entitlements, not data errors.
    const watchedTimes = useWatch({
        control,
        name: ['workerName', 'startTime', 'endTime', 'pauseMinutes']
    });
    const [draftNames, draftStart, draftEnd, draftPause] = watchedTimes;
    const allDay =
        variant.showAllDay && isAllDaySpan(draftStart ?? '', draftEnd ?? '');
    // Read off the times rather than stored, so unticking has to change them:
    // it clears both, which a Gerät may leave blank.
    function setAllDay(checked: boolean) {
        const options = { shouldDirty: true, shouldValidate: true };
        setValue('startTime', checked ? ALL_DAY_START : '', options);
        setValue('endTime', checked ? ALL_DAY_END : '', options);
    }
    // What each offered name is already booked for on THIS day, and whether that
    // collides. Follows the date and time fields: moving the shift re-asks.
    const bookedTimes = useTimeEntryDayBookings(
        variant,
        watchedDate,
        draftStart ?? '',
        draftEnd ?? '',
        resolveAccount,
        entry?.id
    );
    // What the form would SAVE if it were saved now — ONE candidate per picked
    // Mitarbeiter, because each becomes an entry of its own and can be
    // double-booked on its own. Empty while the day is not yet delimited.
    const candidates = useMemo(() => {
        const names = (draftNames ?? [])
            .map((name) => name.trim())
            .filter(Boolean);
        if (names.length === 0 || !draftStart || !draftEnd) {
            return [];
        }
        return names.map((workerName, index) => ({
            // New ones only need ids that tell them apart from each other and
            // from everything already saved.
            id: entry?.id ?? `draft:${index}`,
            entryType: variant.type,
            /**
             * Resolved exactly as the save path does (from the NAME, and never
             * on a variant with no accounts): identity is what both checks
             * aggregate by, so resolving it differently here would judge a
             * different person than the save creates.
             */
            userId: variant.linkUsers
                ? (resolveAccount(workerName)?.id ?? '')
                : '',
            workerName,
            date: watchedDate,
            startTime: draftStart,
            endTime: draftEnd,
            pauseMinutes: Number(draftPause) || 0
        }));
    }, [
        variant,
        entry,
        resolveAccount,
        draftNames,
        draftStart,
        draftEnd,
        draftPause,
        watchedDate
    ]);
    // Every entry these values would collide with — live, so an already
    // conflicting entry says so the moment it is opened, and names the blocker.
    const draftOverlaps = useTimeEntryOverlapsWithEach(candidates);
    // Only while the values it was about are still the ones on screen.
    const shownSaveError =
        saveError &&
        saveError.key ===
            saveErrorKey({
                date: watchedDate,
                workerName: draftNames ?? [],
                startTime: draftStart ?? '',
                endTime: draftEnd ?? ''
            })
            ? saveError.message
            : null;

    // Geräte are out of the ArbZG by definition — it protects people, not
    // machines. The candidates themselves are not gated: one digger cannot be
    // on two sites at once either.
    const draftWarnings = useTimeEntryDraftWarnings(
        variant.linkUsers ? candidates : NO_DRAFTS
    );
    // The inputs those warnings are about, marked amber.
    const warnedFields = useMemo(
        () => timeEntryWarningFields(draftWarnings),
        [draftWarnings]
    );

    // Picking a project is an unsaved edit too — react-hook-form does not see
    // it, so the guard has to be told.
    const closeGuard = useUnsavedCloseGuard({
        open,
        isDirty: isDirty || Boolean(selectedProjectId),
        onClose,
        onSave: () => handleSubmit(onSubmit)()
    });

    async function onSubmit(data: FormValues) {
        if (!collection) {
            return;
        }
        if (!activeProjectId) {
            setProjectError(true);
            return;
        }
        // One entry PER picked Mitarbeiter, sequentially: the double-booking
        // check reads what the previous insert has just written, so two people
        // are checked against each other too.
        const refused: { name: string; message: ReactNode }[] = [];
        let savedCount = 0;
        for (const name of data.workerName) {
            // The shared save path derives year/week + the user link, re-checks
            // the double-booking rule, and writes — for the grid too.
            const result = await saveTimeEntry(
                collection,
                variant,
                resolveAccount,
                activeProjectId,
                { ...data, workerName: name },
                isEdit ? entry!.id : undefined
            );
            if (result.status === 'ok') {
                savedCount += 1;
                continue;
            }
            if (result.status === 'overlap') {
                // The live notice normally already names this entry. It gets
                // its own message only when the live check could not see the
                // blocker: an entry that arrived by replication since the form
                // opened, or one this very save wrote for an earlier name.
                const known = draftOverlaps.some(
                    (overlap) => overlap.id === result.overlap.id
                );
                refused.push({
                    name,
                    message: known ? null : (
                        <TimeEntryOverlapNotice
                            overlap={result.overlap}
                            variant={variant}
                        />
                    )
                });
                continue;
            }
            // The resolver normally catches this inline; surface it too so a
            // blocked save always shows a reason (e.g. end before start).
            refused.push({ name, message: validationMessage(result.message) });
        }

        if (savedCount > 0) {
            notifySaved();
        }
        if (refused.length > 0) {
            // Only the refused names stay in the field: pressing Speichern again
            // then retries exactly them, and never books the saved ones twice.
            const names = refused.map((refusal) => refusal.name);
            setValue('workerName', names);
            // The reasons worth spelling out — a conflict the live panel above
            // already names says nothing new. With nothing to add and nothing
            // saved either, there is no alert at all.
            const details = refused.filter((refusal) => refusal.message);
            const named = data.workerName.length > 1;
            setSaveError({
                key: saveErrorKey({ ...data, workerName: names }),
                message:
                    savedCount === 0 && details.length === 0 ? null : (
                        <>
                            {savedCount > 0 && (
                                <Box>
                                    <FormattedMessage
                                        id="timeEntry.savedPartial"
                                        values={{ count: savedCount }}
                                    />
                                </Box>
                            )}
                            {details.map((refusal) => (
                                <Box key={refusal.name}>
                                    {named && (
                                        <Box
                                            component="span"
                                            sx={{ fontWeight: 'bold' }}
                                        >
                                            {refusal.name}:{' '}
                                        </Box>
                                    )}
                                    {refusal.message}
                                </Box>
                            ))}
                        </>
                    )
            });
            return;
        }
        // Through the guard, never the raw onClose: the guard parks a history
        // sentinel while the dialog is open, and closing past it is what leaves
        // the dialog's route (see useDialogClose).
        closeGuard.close();
    }

    function helper(name: keyof FormValues): string | undefined {
        return errors[name]
            ? validationMessage(errors[name]?.message)
            : undefined;
    }

    return (
        <FormDialog open={open} onClose={closeGuard.requestClose} fullScreen>
            {/* noValidate: react-hook-form + zod own validation. pauseMinutes
                uses step=15 for arrow-stepping, and the browser's native
                `stepMismatch` must not BLOCK a valid non-multiple pause. */}
            <Box
                component="form"
                noValidate
                onSubmit={handleSubmit(onSubmit)}
                sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    height: '100%'
                }}
            >
                <FormDialogAppBar
                    projectId={activeProjectId}
                    onClose={closeGuard.requestClose}
                    title={
                        <FormattedMessage
                            id={
                                isEdit
                                    ? variant.editTitleId
                                    : variant.newTitleId
                            }
                        />
                    }
                />

                <FormSectionsBody>
                    <FormSection>
                        {showProjectSelect && (
                            <Autocomplete
                                options={projectOptions}
                                getOptionLabel={projectLabel}
                                value={selectedProject}
                                onChange={(_event, value) => {
                                    setSelectedProjectId(value?.id ?? '');
                                    setProjectError(false);
                                }}
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        label={intl.formatMessage({
                                            id: 'timeEntry.field.projectId'
                                        })}
                                        placeholder={intl.formatMessage({
                                            id: 'timeEntry.selectProject'
                                        })}
                                        error={projectError}
                                        helperText={
                                            projectError
                                                ? intl.formatMessage({
                                                      id: 'timeEntry.projectRequired'
                                                  })
                                                : undefined
                                        }
                                    />
                                )}
                            />
                        )}
                        <FormRow>
                            <DateStringField
                                name="date"
                                control={control}
                                label={intl.formatMessage({
                                    id: 'timeEntry.field.date'
                                })}
                                disableFuture
                                disableSundays
                            />
                            {/* Geräte only: a Gerät is booked by the piece, so
                                three of one kind for the same span is one row
                                with an Anzahl of 3, and its hours count three
                                times. A person is always one. */}
                            {variant.showQuantity && (
                                <TextField
                                    {...register('quantity', {
                                        setValueAs: (value) =>
                                            value === '' || value === null
                                                ? 1
                                                : Number(value)
                                    })}
                                    type="number"
                                    label={intl.formatMessage({
                                        id: 'timeEntry.field.quantity'
                                    })}
                                    fullWidth
                                    slotProps={{
                                        inputLabel: { shrink: true },
                                        htmlInput: { min: 1, step: 1 }
                                    }}
                                    error={!!errors.quantity}
                                    helperText={helper('quantity')}
                                />
                            )}
                        </FormRow>
                        {/* Von and Bis are short enough to share a row even on
                            a phone; Pause or the checkbox wraps below them
                            there. From sm up all three sit in thirds. */}
                        <FormRow
                            columns={{
                                xs: 'repeat(2, 1fr)',
                                sm: 'repeat(3, 1fr)'
                            }}
                        >
                            <TimeStringField
                                name="startTime"
                                control={control}
                                label={intl.formatMessage({
                                    id: 'timeEntry.field.startTime'
                                })}
                                maxTime={allDay ? undefined : maxTime}
                                warning={warnedFields.has('startTime')}
                                disabled={allDay}
                            />
                            <TimeStringField
                                name="endTime"
                                control={control}
                                label={intl.formatMessage({
                                    id: 'timeEntry.field.endTime'
                                })}
                                maxTime={allDay ? undefined : maxTime}
                                warning={warnedFields.has('endTime')}
                                disabled={allDay}
                            />
                            {variant.showAllDay && (
                                <FormControlLabel
                                    // The height of the pickers beside it,
                                    // so the box sits level with them.
                                    // Only its own width, not the whole cell.
                                    sx={{
                                        height: 56,
                                        mr: 0,
                                        justifySelf: 'start'
                                    }}
                                    control={
                                        <Checkbox
                                            checked={allDay}
                                            onChange={(event) =>
                                                setAllDay(event.target.checked)
                                            }
                                        />
                                    }
                                    label={intl.formatMessage({
                                        id: 'timeEntry.field.allDay'
                                    })}
                                />
                            )}
                            {/* Machines take no break: Geräte rows keep a
                                pause of 0. */}
                            {variant.showPause && (
                                <TextField
                                    {...register('pauseMinutes', {
                                        setValueAs: (value) =>
                                            value === '' || value === null
                                                ? 0
                                                : Number(value)
                                    })}
                                    type="number"
                                    label={intl.formatMessage({
                                        id: 'timeEntry.field.pauseMinutes'
                                    })}
                                    fullWidth
                                    slotProps={{
                                        inputLabel: { shrink: true },
                                        htmlInput: { min: 0, step: 15 }
                                    }}
                                    error={!!errors.pauseMinutes}
                                    helperText={helper('pauseMinutes')}
                                    {...(warnedFields.has('pauseMinutes') &&
                                    !errors.pauseMinutes
                                        ? {
                                              color: 'warning' as const,
                                              focused: true
                                          }
                                        : {})}
                                />
                            )}
                        </FormRow>
                        {/* Last because the times above decide who can be picked
                            at all. Alone on the row because a whole crew is
                            picked here: a dozen names in a third of the width is
                            a column of chips two words wide, and the bracket
                            behind each name needs the room too. */}
                        <FormRow>
                            <TimeEntryNameField
                                control={control}
                                variant={variant}
                                options={nameOptions}
                                multiple={multipleNames}
                                bookedTimes={bookedTimes}
                            />
                        </FormRow>
                        {/* No phase field at all. An override already on the
                            entry is preserved (it is still in the form's
                            values), just not shown. */}
                        <TextField
                            {...register('note')}
                            label={intl.formatMessage({
                                id: 'timeEntry.field.note'
                            })}
                            fullWidth
                            multiline
                            minRows={3}
                            error={!!errors.note}
                            helperText={helper('note')}
                        />
                    </FormSection>

                    {/* The double-booking itself: an error, not a hint, and
                        therefore above the advisory ones. Shown while the values
                        collide, and each conflict links to the entry in the
                        way. */}
                    {draftOverlaps.length > 0 && (
                        <FormSectionNotice>
                            <Alert severity="error">
                                <AlertTitle>
                                    <FormattedMessage id="timeEntry.conflict" />
                                </AlertTitle>
                                {draftOverlaps.map((overlap) => (
                                    <TimeEntryOverlapNotice
                                        key={overlap.id}
                                        overlap={overlap}
                                        variant={variant}
                                    />
                                ))}
                            </Alert>
                        </FormSectionNotice>
                    )}

                    {shownSaveError && (
                        <FormSectionNotice>
                            <Alert severity="error">{shownSaveError}</Alert>
                        </FormSectionNotice>
                    )}

                    {/* Statutory hints, never a blocker. They sit BELOW the
                        fields: they comment on what was entered, and on top they
                        would push the form down every time a value crosses a
                        limit. Not dismissible — they follow the values. */}
                    {draftWarnings.length > 0 && (
                        <FormSectionNotice>
                            <Alert severity="warning">
                                <AlertTitle>
                                    <FormattedMessage id="timeEntry.warning.title" />
                                </AlertTitle>
                                {draftWarnings.map((code) => (
                                    <Box key={code}>
                                        <FormattedMessage
                                            id={`timeEntry.warning.${code}`}
                                        />
                                    </Box>
                                ))}
                            </Alert>
                        </FormSectionNotice>
                    )}
                </FormSectionsBody>

                <FormDialogFooter
                    onCancel={closeGuard.requestClose}
                    isSubmitting={isSubmitting}
                />
            </Box>
            <UnsavedChangesDialog {...closeGuard.confirmProps} />
        </FormDialog>
    );
}
